import {
  PageResponse,
  WriteResponse,
  Response,
  PageRequest,
} from "@/types/api-types";
import { deleteOne, fetchStream, get, post, put } from "@/modules/request";
import {
  ChatScenePosition,
  ChatSceneWithChatInfo,
  GenerateMateContext,
  MatePersona,
  MateWithChatInfo,
} from "@/types/chat";
import {
  ChatMessageImageType,
  InputTypeEnum,
  MateCardFactoryGenerateType,
  MateCardPromptType,
} from "@/enums/chatEnums";
import { ChatMessage, ModuleEntityType } from "@/types/entity";

export function readMatePopularPageFroChat(params: {
  filter?: {
    isRecommended?: 0 | 1;
  };
  pagination: {
    page: number;
    perPage: number;
  };
}): Promise<PageResponse<MateWithChatInfo>> {
  return get("/Mate/ReadPopularPage", {
    tab: "chat",
    ...params,
  });
}

export function createOneChatImageMessage(
  id: number,
  param: { type: ChatMessageImageType }
): Promise<WriteResponse<ChatMessage>> {
  return post(`/ChatMessageImage/${id}`, param);
}

export function createOneCardPrompt(param: {
  text?: string;
  isUniversal?: boolean;
  method: MateCardPromptType;
  type: MateCardFactoryGenerateType;
  mate: GenerateMateContext;
  from?: number | null;
  to?: number | null;
  chatSceneName?: string;
  chatSceneDescription?: string;
  name?: string;
}): Promise<Response<{ text: string }>> {
  return post(`/Mate/CardPrompt`, param);
}

export function postMateModuleComplete(param: {
  entityId: number;
  entityType: ModuleEntityType;
  moduleName: string;
  questionText: string;
  text?: string;
  type: MateCardFactoryGenerateType;
}): Promise<Response<{ text: string }>> {
  return post(`/MateModuleComplete`, param);
}

export function postMateModuleCompleteSteam(
  params: Parameters<typeof postMateModuleComplete>[0],
  cb: (content: Response<{ text: string }> | string) => void
) {
  return fetchStream(`/MateModuleComplete`, "POST", params, cb);
}

export const ApiGetChatReplyOptions = (params: {
  mateSessionId: number;
  type?: InputTypeEnum;
  temperature?: number;
}): Promise<Response<{ text: string }[]>> => {
  const query = new URLSearchParams();
  Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .forEach(([key, value]) => {
      query.append(key, value.toString());
    });
  return get(`/GetChatReplyOptions?${query.toString()}`);
};

export const createOneMatePersona = (param: {
  name: string;
  description: string;
  mediaId: number;
  personalityPrompt: string;
  systemPrompt?: string;
}): Promise<WriteResponse<MatePersona>> => {
  return post(`/MatePersona`, param);
};

export const updateOneMatePersona = (
  id: number,
  param: {
    name?: string;
    description?: string;
    mediaId?: number;
    personalityPrompt?: string;
    systemPrompt?: string;
  }
): Promise<WriteResponse<MatePersona>> => {
  return put(`/MatePersona/${id}`, param);
};

export const deleteOneMatePersona = (id: number): Promise<Response<null>> => {
  return deleteOne(`/MatePersona/${id}`);
};

export const readMatePersonaPage = (
  params: PageRequest<{ mateId?: number }>
): Promise<PageResponse<MatePersona>> => {
  return get("/MatePersonaPage", params);
};

export const readChatImageCount = (
  uuid: string
): Promise<Response<{ count: number }>> => {
  return get(`/Mate/ReadChatImageCount/${uuid}`);
};

export const readPopularChatScene = (
  params: PageRequest<{
    isRecommended?: 0 | 1;
  }>
): Promise<PageResponse<ChatSceneWithChatInfo>> => {
  return get("/ChatScene/ReadPopularPage", params);
};

export const updateOneChatSceneStatus = (
  id: number
): Promise<WriteResponse<any>> => {
  return post(`/MateSession/ChatSceneStatus/${id}`);
};

export const createOneChatScenePosition = (param: {
  chatSceneId: number;
}): Promise<WriteResponse<ChatScenePosition>> => {
  return post(`/ChatScenePosition`, param);
};

export const updateOneChatScenePosition = (
  _params: Partial<ChatScenePosition>
): Promise<WriteResponse<ChatScenePosition>> => {
  const {
    name,
    parentId,
    shortDescription,
    enterCondition,
    leavingDefinition,
    characterzz,
    destinationzz,
  } = _params;

  return put(`/ChatScenePosition/${_params.id!}`, {
    name,
    parentId,
    shortDescription,
    enterCondition,
    leavingDefinition,
    characterzz: characterzz?.map((v) => ({ id: v.id })),
    destinationzz: destinationzz?.map((v) => ({ id: v.id })),
  });
};

export const deleteOneChatScenePosition = (
  id: number
): Promise<Response<null>> => deleteOne(`/ChatScenePosition/${id}`);
